import { CurrentFlag } from 'entities/CurrentFlag';

export const getCurrentFlag = (currentFlagList: CurrentFlag[]) => {
  const today = new Date();
  const month = today.getMonth();
  const year = today.getFullYear();

  const date = new Date(year, month, 1);

  const currList = currentFlagList.filter((x) => new Date(x.fromDate) >= date);

  return currList[0];
};

export const getRefMonthFlag = (
  refMonth: string,
  currentFlagList: CurrentFlag[]
) => {
  const [year, month] = refMonth.split('_').map(Number);
  const target = year * 12 + (month - 1);

  // Bandeira vigente no mês de referência = a de maior fromDate cujo ano/mês
  // é <= o mês pedido. Comparação por ano-mês em UTC (getUTC*) — a comparação
  // antiga usava datas locais e o fromDate (00:00Z) ficava "antes" da meia-noite
  // local no Brasil (UTC-3), retornando sempre undefined → bandeira sem cor.
  // Também é robusta quando a bandeira começa num mês e continua nos seguintes.
  let flag: CurrentFlag | undefined;
  let bestKey = -Infinity;
  for (const x of currentFlagList) {
    const d = new Date(x.fromDate);
    const key = d.getUTCFullYear() * 12 + d.getUTCMonth();
    if (key <= target && key > bestKey) {
      flag = x;
      bestKey = key;
    }
  }

  return flag;
};

export interface FlagPeriod {
  slug: string;
  name: string;
  from: Date; // primeiro dia (inclusive) em que a bandeira vigorou NO ciclo
  to: Date; // último dia (inclusive) em que a bandeira vigorou NO ciclo
  days: number;
}

// Datas ancoradas em UTC: `fromDate` (coluna `date`) e `read.*` (UTC meio-dia)
// chegam como instantes UTC; usar componentes LOCAIS retornaria o dia anterior no
// Brasil (UTC-3) — mesmo motivo de `getRefMonthFlag`/`format.date` usarem UTC.
const toDay = (d: Date) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
const addDays = (d: Date, n: number) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n));
const dayDiff = (a: Date, b: Date) =>
  Math.round((toDay(a).getTime() - toDay(b).getTime()) / 86_400_000);

/**
 * Datas de vigência de cada bandeira DENTRO do ciclo de faturamento, derivadas do
 * calendário oficial ANEEL (`current_flags`, Redux) — NÃO do `periodFlags` do
 * extrator (que só captura uma bandeira). Assim exibimos a(s) bandeira(s) sem
 * depender do regex de cada distribuidora; a validação da bandeira segue pelo
 * valor cobrado (auditoria).
 *
 * Convenção de faturamento `(previousDate, currentDate]`: o dia da leitura
 * anterior pertence ao ciclo passado; conta-se do dia SEGUINTE à leitura anterior
 * até a leitura atual (inclusive) — mesma regra do `prorateByVigencia` do backend.
 * Uma bandeira que passa a vigorar no dia B faz o dia B já usar a nova.
 *
 * Fallback: sem datas de leitura válidas, estima o ciclo pelo `refMonth`
 * (1º dia do mês anterior → último dia do mês de referência), igual ao backend.
 */
export const getFlagPeriods = (
  previousDate: Date | string | undefined,
  currentDate: Date | string | undefined,
  refMonth: string | undefined,
  currentFlagList: CurrentFlag[] = []
): FlagPeriod[] => {
  let prev = previousDate ? new Date(previousDate) : new Date(NaN);
  let curr = currentDate ? new Date(currentDate) : new Date(NaN);

  const validRead =
    !Number.isNaN(prev.getTime()) &&
    !Number.isNaN(curr.getTime()) &&
    dayDiff(curr, prev) > 0;

  if (!validRead && refMonth) {
    const [y, m] = refMonth.split('_').map(Number);
    if (y && m) {
      prev = new Date(Date.UTC(y, m - 2, 1));
      curr = new Date(Date.UTC(y, m, 0));
    }
  }

  if (dayDiff(curr, prev) <= 0 || currentFlagList.length === 0) return [];

  const cycleStart = addDays(prev, 1); // 1º dia faturado (leitura anterior não conta)
  const cycleEnd = toDay(curr); // último dia faturado (inclusive)

  const sorted = currentFlagList
    .map((f) => ({ f, start: toDay(new Date(f.fromDate)) }))
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const periods: FlagPeriod[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const start = sorted[i].start;
    const nextStart = i + 1 < sorted.length ? sorted[i + 1].start : null;
    // Recorte da vigência [start, nextStart) contra o ciclo [cycleStart, cycleEnd]
    const segStart =
      start.getTime() > cycleStart.getTime() ? start : cycleStart;
    const vigEnd = nextStart ? addDays(nextStart, -1) : cycleEnd;
    const segEnd = vigEnd.getTime() < cycleEnd.getTime() ? vigEnd : cycleEnd;
    if (segStart.getTime() > segEnd.getTime()) continue; // não vigora neste ciclo
    periods.push({
      slug: sorted[i].f.flag?.slug ?? '',
      name: sorted[i].f.flag?.name ?? sorted[i].f.flag?.slug ?? '',
      from: segStart,
      to: segEnd,
      days: dayDiff(segEnd, segStart) + 1, // ambos inclusivos
    });
  }
  return periods;
};

const dm = (d: Date) =>
  `${String(d.getUTCDate()).padStart(2, '0')}/${String(
    d.getUTCMonth() + 1
  ).padStart(2, '0')}`;

/** "Amarela · 16/12–31/12 (16 dias)" — rótulo por bandeira. `label` sobrepõe o nome. */
export const formatFlagPeriod = (p: FlagPeriod, label?: string): string =>
  `${label ?? p.name} · ${dm(p.from)}–${dm(p.to)} (${p.days} dia${
    p.days > 1 ? 's' : ''
  })`;
